import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import "./Login.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { Spin } from "antd";
import { useBoundStore } from "store";
import {
  buildOrganizationsPath,
} from "shared/utils/paths";

const AUTH_TIMEOUT_MS = 10_000;

export const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, loginWithRedirect, error, logout } = useAuth0();
  const { tenant } = useBoundStore();
  const [timedOut, setTimedOut] = useState(false);

  // Timeout: if Auth0 is stuck loading for too long, force a clean re-login
  useEffect(() => {
    if (!isLoading) {
      setTimedOut(false);
      return;
    }

    const timer = setTimeout(() => setTimedOut(true), AUTH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || error) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const invitation = params.get("invitation");
    const organization = params.get("organization");

    // Already signed in and no invitation to redeem — nothing to do here; the
    // navigation effect below routes into the app.
    if (isAuthenticated && !invitation) {
      return;
    }

    // An invitation arrived while a different session is active. Auth0 won't show
    // the invitee's signup screen over an existing SSO session, so clear it and
    // return to this same URL (params intact). The post-logout reload then runs
    // the normal unauthenticated invite flow below.
    if (isAuthenticated && invitation) {
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}${location.pathname}${location.search}`,
        },
      });
      return;
    }

    loginWithRedirect({
      appState: { returnTo: location.pathname },
      authorizationParams: {
        ...(invitation ? { invitation } : {}),
        ...(organization ? { organization } : {}),
        // For an invitation, let Auth0 show signup/set-password; otherwise normal login.
        screen_hint: invitation ? "signup" : "login",
      },
    });
  }, [error, isAuthenticated, isLoading, location.pathname, location.search, loginWithRedirect, logout]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !tenant?.id) return;
    // A pending invitation in the URL takes precedence — let the effect above
    // redeem it instead of routing into the current account.
    if (new URLSearchParams(location.search).has("invitation")) return;
    navigate(buildOrganizationsPath(tenant.id), { replace: true });
  }, [isLoading, isAuthenticated, tenant, navigate, location.search]);

  if (timedOut) {
    // Auth0 stuck — clear stale tokens and retry
    logout({ logoutParams: { returnTo: window.location.origin } });
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
  }

  if (isLoading) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
  }

  if (error) {
    return <div>Auth error: {error.message}</div>;
  }

  // Show spinner while loginWithRedirect is in progress
  return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><Spin /></div>;
};
